import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import { getApiErrorMessage } from '../helpers/apiErrors';
import { ConfirmModalModel, LoginCredential } from '../models';

export interface GlobalState {
  currentTheme: string;
  navigationPath?: string;
  errorMessage?: string;
  credentialResponse?: LoginCredential; // Consider using a more specific type if available
  businessEmailAddress?: string;
  isChatOpen: boolean;
  isNavOpen: boolean;
  confirmModalProps?: ConfirmModalModel;
  audioPreferences: {
    voiceEnabled: boolean;
    microphonePermissionGranted: boolean;
    preferredInputDeviceId?: string;
    preferredOutputDeviceId?: string;
    registrationPreflightCompleted: boolean;
  };
}

const initialState: GlobalState = {
  currentTheme: 'light',
  navigationPath: undefined,
  errorMessage: undefined,
  credentialResponse: undefined,
  businessEmailAddress: undefined,
  isChatOpen: false,
  isNavOpen: false,
  confirmModalProps: undefined,
  audioPreferences: {
    voiceEnabled: true,
    microphonePermissionGranted: false,
    preferredInputDeviceId: undefined,
    preferredOutputDeviceId: undefined,
    registrationPreflightCompleted: false,
  },
};

const requireAudioPreferences = (
  audioPreferences: GlobalState['audioPreferences'] | undefined
): GlobalState['audioPreferences'] => {
  if (!audioPreferences) {
    throw new Error(
      'Invalid persisted global state: audioPreferences is missing. Clear localStorage key persist:project-secretary-root-v2 and reload.'
    );
  }

  return audioPreferences;
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setCurrentTheme: (state, action: PayloadAction<string>) => {
      state.currentTheme = action.payload;
      return state;
    },
    setLogoutNavPath: (state, action: PayloadAction<string | undefined>) => {
      state.navigationPath = action.payload ? action.payload : undefined;
      return state;
    },
    setError: (state, action: PayloadAction<{ reset: boolean, e?: any }>) => {
      const newMessage = action.payload.reset
        ? undefined
        : getApiErrorMessage(action.payload.e);

      // Prevent duplicate toasts
      if (newMessage !== state.errorMessage) {
        state.errorMessage = newMessage;
      } else {
        state.errorMessage = undefined;
      }

      return state;
    },
    setLogin: (state, action: PayloadAction<any>) => {
      state.credentialResponse = action.payload;
      return state;
    },
    setChatOpen: (state, action: PayloadAction<boolean>) => {
      state.isChatOpen = action.payload;
      return state;
    },
    toggleChat: (state) => {
      state.isChatOpen = !state.isChatOpen;
      return state;
    },
    toggleNav: (state) => {
      state.isNavOpen = !state.isNavOpen;
      return state;
    },
    setNavOpen: (state, action: PayloadAction<boolean>) => {
      state.isNavOpen = action.payload;
      return state;
    },
    setConfirmModalProps: (state, action: PayloadAction<ConfirmModalModel | undefined>) => {
      state.confirmModalProps = action.payload;
      return state;
    },
    setAudioPreferences: (state, action: PayloadAction<Partial<GlobalState['audioPreferences']>>) => {
      state.audioPreferences = {
        ...state.audioPreferences,
        ...action.payload,
      };
      return state;
    },
    setLogoutGlobal: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setCurrentTheme, setLogoutNavPath, setError, setLogin, setChatOpen, setLogoutGlobal, toggleChat, setConfirmModalProps, toggleNav, setNavOpen, setAudioPreferences } = globalSlice.actions;

export const selectCurrentTheme = (state: RootState) => state.global.currentTheme;
export const selectErrorMessage = (state: RootState) =>(state.global.errorMessage);
export const selectLoginCredential = (state: RootState) => state.global.credentialResponse;
export const selectIsNavOpen = (state: RootState) => state.global.isNavOpen;
export const selectAudioPreferences = (state: RootState) => requireAudioPreferences(state.global.audioPreferences);


export default globalSlice.reducer;
